// External Libraries
import React from "react";
import DatePicker from "react-datepicker";
import * as Icon from "react-feather";
import Modal from "react-responsive-modal";
import { Tooltip } from "react-tippy";

// Constants
import CLIENTS from "../../../constants/Clients";

// Services
import UserService from "../../../services/UserService";
import UtilityService from "../../../services/UtilityService";
import ClientService from "../../../services/ClientService";
import ContactService from "../../../services/ContactService";

// Scenes
import Tasks from "../../tasks/Tasks";
import Notes from "../../notes/Notes";
import Engagements from "../../workflow/Engagements";
import BackofficeEngagements from "../../backoffice/engagements/BackofficeEngagements";
import Contacts from "../Contacts/Contacts";
import Tools from "../Tools/Tools";
import Connections from "../Connections/Connections";
import CommunicationLog from "../Communications/CommunicationLog";
import ScheduleMeetingModal from "../Communications/ScheduleMeetingModal";

// Common Components
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import TextAreaField from "../../common/text_area_field/TextAreaField";
import Checkbox from "../../common/checkbox/Checkbox";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import DateSelector from "../../common/date_selector/DateSelector";
import WComponent from "../../common/WComponent";
import Toggle from "../../common/toggle/Toggle";
import DateInput from "../../common/date_input/DateInput";
import Button from "../../common/button/Button";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/tab/Tab";
import PageHeading from "../../common/page_heading/PageHeading";

// Styles
import "./clientDetails.css";
import RestrictedAccess from "../../common/restricted_access/RestrictedAccess";
import PermissionGate from "../../../permissions/permissionGate";
import RequiredLabel from "../../common/required_label";
import ClientGroups from "../ClientGroups/ClientGroups";

const { alert } = UtilityService;

class ClientDetails extends WComponent {
	constructor() {
		super();
		this.state = {
			search: "",
			loading: false,
			type: "corporate",

			teamId: 0,
			partnerId: 0,
			name: "",
			medium: "",
			yearEnd: "",
			address: "",
			city: "",
			stateProv: "",
			zipPostal: "",
			notes: "",
			craBusinessNumber: "",
			isHst: false,
			isPayroll: false,
			isEht: false,
			isWsib: false,
			faxNumber: "",
			operatingName: "",

			partners: [],
			users: [],
			engagements: [],

			view: "details",
			// Which list the Engagements tab shows: the client's workflow engagements, or its
			// backoffice work rolled up into engagements (read-only, for reporting).
			engagementsMode: "workflow",
			showModal: false,
			showDeleteModal: false,
			showScheduleModal: false,

			statuses: CLIENTS.statuses,
			status: null,

			managerId: 0,
			seniorManagerId: 0,
			bookkeeperManagerId: 0,
			managedByRole: null,

			customFields: [],

			email: "",
			description: "",

			selectedTeamName: "",
			selectedPartnerName: "",
			selectedSeniorManagerName: "",
			selectedManagerName: "",
			selectedBookkeeperManagerName: "",

			clientGroupId: 0,
			selectedClientGroupName: "",
			selectedStatusName: "",
			isNotAllowedResource : false,
			hstNumber: "",

			showNewGroupModal: false,
			newGroupName: "",
			creatingGroup: false,
			primaryContactPhone: "",
			primaryContactToggle: false,
			primaryContactId: null
		};
	}

	componentDidMount = () => {
		this.resetComponent();
	};

	componentDidUpdate = prevProps => {
		let { clientId } = this.props.match.params;

		if (prevProps.match.params.clientId !== clientId) {
			this.resetComponent();
		}
	};

	resetComponent = async () => {
		let { clientType, clientId } = this.props.match.params;

		await this.update({
			type: clientType,
			clientId: clientId,
			loading: true
		});

		// Call react-alert to notify client that it is loading
		alert({ type: "info", text: "Loading..." });

		if (clientId !== "new") {
			await this.fetchClient();
			await this.fetchPrimaryContact({ clientId });
		}

		await this.fetchPartners();
		await this.fetchUsers();
		await this.fetchCustomFields();

		await this.update({ loading: false });
	};

	fetchPartners = async () => {
		let partners = await UserService.fetchPartners("Unassigned");

		this.update({
			partners: partners
		});
	};

	fetchUsers = async () => {
		let users = await UserService.fetchUsers({});
		users.unshift({ key: 0, value: "Unassigned" });
		this.update({
			users: users
		});
	};

	fetchClient = async () => {
		let { clientId } = this.state;

		try {
			let response = await ClientService.getClient({ clientId });

			if (UtilityService.isNotAllowedResource({ response : response.data })) {
				this.update({
					isNotAllowedResource: true
				});
				return;
			}

			let client = response.data.client;
			let engagements = response.data.engagements;

			// React Date Picker does not like 0000-00-00 as the date
			if (client.year_end === "0000-00-00") {
				client.year_end = "2000-01-01";
			}

			let selectedPartnerName = "";
			let selectedSeniorManagerName = "";
			let selectedManagerName = "";
			let selectedBookkeeperManagerName = "";
			let selectedClientGroupName = "";
			let selectedStatusName = "";

			if (UtilityService.isNotNullish(client.User)) {
				selectedPartnerName = `${client.User.first_name} ${client.User.last_name}`;
			}

			if (UtilityService.isNotNullish(client.senior_manager)) {
				selectedSeniorManagerName = `${client.senior_manager.first_name} ${client.senior_manager.last_name}`;
			}

			if (UtilityService.isNotNullish(client.manager)) {
				selectedManagerName = `${client.manager.first_name} ${client.manager.last_name}`;
			}

			if (UtilityService.isNotNullish(client.bookkeeper_manager)) {
				selectedBookkeeperManagerName = `${client.bookkeeper_manager.first_name} ${client.bookkeeper_manager.last_name}`;
			}

			if (UtilityService.isNotNullish(client.client_group_id)) {
				selectedClientGroupName = client.client_group.name;
			}

			if (UtilityService.isNotNullish(client.status)) {
				const statusKey = client.status === "deleted" ? "soft_deleted" : client.status;
				selectedStatusName = CLIENTS.statuses.find(status => status.key === statusKey)?.value || "";
			}

			this.update({
				teamId: client.team_id,
				clientGroupId: client.client_group_id,
				partnerId: client.partner_id,
				managerId: client.manager_id,
				seniorManagerId: client.senior_manager_id,
				bookkeeperManagerId: client.bookkeeper_manager_id,
				managedByRole: client.managed_by_role || null,
				type: client.type,
				name: client.name,
				medium: client.medium,
				engagements: engagements,
				yearEnd: client.year_end,
				notes: client.notes,
				address: client.address,
				city: client.city,
				stateProv: client.state_prov,
				zipPostal: client.zip_postal,
				craBusinessNumber: client.cra_business_number,
				isHst: client.is_hst,
				isPayroll: client.is_payroll,
				isEht: client.is_eht,
				isWsib: client.is_wsib,
				faxNumber: client.fax_number,
				operatingName: client.operating_name,
				status: client.status,
				email: client.email,
				hstNumber: client.hst_number,
				description: client.description,

				selectedPartnerName,
				selectedSeniorManagerName,
				selectedManagerName,
				selectedBookkeeperManagerName,
				selectedClientGroupName,
				selectedStatusName
			});

			if (client.team) {
				this.update({
					selectedTeamName: client.team.name
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	fetchPrimaryContact = async ({ clientId }) => {
		try {
			let response = await ContactService.getListOfContacts({ clientId });
			let contacts = response?.data || [];
			let primary = contacts.find(c => c.Clients && c.Clients[0] && c.Clients[0].ContactClient.is_primary);
			if (primary) {
				this.update({
					primaryContactPhone: primary.phone || "",
					primaryContactToggle: true,
					primaryContactId: primary.id
				});
			} else {
				this.update({
					primaryContactPhone: "",
					primaryContactToggle: false,
					primaryContactId: null
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	savePrimaryContact = async ({ clientId }) => {
		let { primaryContactPhone, primaryContactToggle, primaryContactId, name, email, address, type, craBusinessNumber } = this.state;

		if (!primaryContactToggle || !primaryContactPhone) return;

		const nameParts = (name || "").trim().split(" "); //because contact requires to save name as first and lastname
		const firstName = nameParts[0] || "";
		const lastName = nameParts.slice(1).join(" ") || "";
		const sin = type === CLIENTS.clientTypes.personal.id ? craBusinessNumber : undefined;

		if (primaryContactId) {
			await ContactService.updateContact({
				contactId: primaryContactId,
				clientId,
				firstName,
				lastName,
				email,
				phone: primaryContactPhone,
				address,
				isPrimary: true,
				sin
			});
		} else {
			await ContactService.createContact({
				clientId,
				firstName,
				lastName,
				email,
				phone: primaryContactPhone,
				address,
				isPrimary: true,
				sin
			});
		}
	};

	updateClient = async () => {
		let {
			clientId,
			clientGroupId,
			partnerId,
			managerId,
			name,
			yearEnd,
			address,
			city,
			stateProv,
			zipPostal,
			notes,
			craBusinessNumber,
			isHst,
			isPayroll,
			isEht,
			isWsib,
			faxNumber,
			operatingName,
			status,
			customFields,
			email,
			description,
			loading,
			teamId,
			seniorManagerId,
			bookkeeperManagerId,
			managedByRole,
			hstNumber
		} = this.state;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return;
		}

		if (loading) {
			// Call react-alert saying the client is being updated
			alert({ type: "info", text: "Updating..." });
			return;
		}

		this.update({
			loading: true
		});

		try {
			await ClientService.updateClient({
				clientGroupId,
				teamId,
				seniorManagerId,
				bookkeeperManagerId,
				clientId,
				partnerId,
				managerId,
				managedByRole,
				name,
				yearEnd,
				address,
				city,
				stateProv,
				zipPostal,
				notes,
				craBusinessNumber,
				isHst,
				isPayroll,
				isEht,
				isWsib,
				faxNumber,
				operatingName,
				status,
				customFields,
				email,
				description,
				hstNumber
			});

			await this.savePrimaryContact({ clientId });
			alert({ type: "success", text: "Updated Successfully!" });
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Update Error!" });
		}

		this.update({
			loading: false
		});
	};

	createClient = async () => {
		let {
			clientGroupId,
			partnerId,
			managerId,
			name,
			yearEnd,
			address,
			city,
			stateProv,
			zipPostal,
			notes,
			craBusinessNumber,
			isHst,
			isPayroll,
			isEht,
			isWsib,
			faxNumber,
			operatingName,
			customFields,
			email,
			description,
			loading,
			type,
			teamId,
			seniorManagerId,
			bookkeeperManagerId,
			managedByRole,
			hstNumber
		} = this.state;

		let { history } = this.props;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return;
		}

		if (loading) {
			alert({ type: "info", text: "Updating..." });
			return;
		}

		this.update({
			loading: true
		});

		try {
			let response = await ClientService.createClient({
				type,
				clientGroupId,
				partnerId,
				managerId,
				managedByRole,
				name,
				yearEnd,
				address,
				city,
				stateProv,
				zipPostal,
				notes,
				craBusinessNumber,
				isHst,
				isPayroll,
				isEht,
				isWsib,
				faxNumber,
				operatingName,
				teamId,
				seniorManagerId,
				bookkeeperManagerId,
				customFields,
				email,
				description,
				hstNumber
			});

			this.update({
				loading: false
			});

			await this.savePrimaryContact({ clientId: response.data.id });
			alert({ type: "success", text: "Created Successfully!" });
			alert({ type: "info", text: "Google Drive folder is being created and linked automatically." });
			history.push(`/clients/${type}/${response.data.id}`);
		} catch (error) {
			alert({ type: "error", text: "Update Error!" });
			console.log(error);
		}

		this.update({
			loading: false
		});
	};

	onDeleteClient = async () => {
		let { clientId } = this.state;

		this.update({
			loading: true
		});

		let success = await ClientService.deleteClient({ clientId });

		if (success) {
			alert({ type: "success", text: "Deleted Successfully!" });
		} else {
			alert({ type: "error", text: "Delete Error!" });
		}

		this.update({
			loading: false
		});
	};

	onChange = e => {
		let target = e.target;
		let value = target.type === "checkbox" ? target.checked : target.value;

		if (target.name === "craBusinessNumber") {
			if (!/^[0-9]*$/.test(value)) return; // allow only 0–9 digits
		}

		if (target.name === "hstNumber" && value.length > 9) {
			return; // limit to 9 characters
		}

		this.update({
			[target.name]: value
		});
	};

	onClientGroupSelect = item => {
		this.update({
			clientGroupId: item.id,
			selectedClientGroupName: item.name
		});
	};

	onOpenNewGroupModal = () => {
		this.update({
			showNewGroupModal: true,
			newGroupName: ""
		});
	};

	onCloseNewGroupModal = () => {
		this.update({
			showNewGroupModal: false,
			newGroupName: ""
		});
	};

	onCreateNewGroup = async () => {
		let { creatingGroup, newGroupName } = this.state;

		if (creatingGroup) {
			return;
		}

		let name = (newGroupName || "").trim();
		if (!name) {
			alert({ type: "error", text: "Please enter a client group name." });
			return;
		}

		await this.update({ creatingGroup: true });

		// The newly created group is selected for this client, and the client is persisted in this group
		let clientGroup = await ClientService.createClientGroup({ name, assignedClients: [] });

		await this.update({ creatingGroup: false });

		if (!clientGroup) {
			alert({ type: "error", text: "Failed to create client group." });
			return;
		}

		await this.update({
			clientGroupId: clientGroup.id,
			selectedClientGroupName: clientGroup.name,
			showNewGroupModal: false,
			newGroupName: ""
		});

		alert({ type: "success", text: "Client group created!" });
	};

	onPartnerSelect = item => {
		this.update({
			partnerId: item.id,
			selectedPartnerName: item.name
		});
	};

	onManagerSelect = item => {
		this.update({
			managerId: item.id,
			selectedManagerName: item.name,
			managedByRole: this.getManagedByRoleAfterSlotChange(CLIENTS.managedByRoles.manager, item.id)
		});
	};

	onSeniorManagerSelect = item => {
		this.update({
			seniorManagerId: item.id,
			selectedSeniorManagerName: item.name,
			managedByRole: this.getManagedByRoleAfterSlotChange(CLIENTS.managedByRoles.seniorManager, item.id)
		});
	};

	onBookkeeperManagerSelect = item => {
		this.update({
			bookkeeperManagerId: item.id,
			selectedBookkeeperManagerName: item.name,
			managedByRole: this.getManagedByRoleAfterSlotChange(CLIENTS.managedByRoles.bookkeeperManager, item.id)
		});
	};

	// Clearing a manager field also clears its "Managed by" checkbox.
	getManagedByRoleAfterSlotChange = (role, userId) => {
		let { managedByRole } = this.state;
		return managedByRole === role && !userId ? null : managedByRole;
	};

	onManagedByToggle = role => {
		let { managedByRole } = this.state;
		this.update({ managedByRole: managedByRole === role ? null : role });
	};

	renderManagedByCheckbox = role => {
		let { managedByRole, seniorManagerId, managerId, bookkeeperManagerId } = this.state;

		const slotUserIds = {
			[CLIENTS.managedByRoles.seniorManager]: seniorManagerId,
			[CLIENTS.managedByRoles.manager]: managerId,
			[CLIENTS.managedByRoles.bookkeeperManager]: bookkeeperManagerId
		};

		if (!slotUserIds[role]) {
			return null;
		}

		return (
			<div className="details__general__managed-by">
				<Checkbox title="Managed by" name={`managedBy-${role}`} checked={managedByRole === role} onChange={() => this.onManagedByToggle(role)} isSmall />
				<Tooltip
					arrow
					title="Marks this person as the one who manages this client day-to-day — teammates should contact them about this client. Only one can be selected, and it does not change their role."
				>
					<Icon.Info size={13} className="details__general__managed-by__info" />
				</Tooltip>
			</div>
		);
	};

	onStatusSelect = item => {
		this.update({ status: item.id, selectedStatusName: item.name });
	};

	onYearEndSelect = date => {
		if (!date) {
			this.update({ yearEnd: "" });
			return;
		}

		const baseYear = 2000;
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");

		this.update({ yearEnd: `${baseYear}-${month}-${day}` });
	};

	onSelectTeam = async team => {
		await this.update({
			teamId: team.id,
			selectedTeamName: team.name
		});
	};

	onBack = () => {
		let { history } = this.props;
		history.goBack();
	};

	isNewClient = () => {
		let { clientId } = this.state;
		return clientId === "new";
	};

	isDisabled = () => {
		let { loading } = this.state;
		return !this.isValid() || loading;
	};

	isValid = () => this.getMissingRequiredFields().length === 0;

	onConfirmModalOpen = () => {
		this.update({
			showDeleteModal: true
		});
	};

	onCloseDeleteModal = ({ confirmed }) => {
		this.update({
			showDeleteModal: false
		});

		let { clientType } = this.props.match.params;
		let { history } = this.props;

		if (confirmed === true) {
			this.onDeleteClient();
			history.push({
				pathname: `/clients/${clientType}`,
				state: { resetComponent: true }
			});
		}
	};

	switchView = view => {
		this.update({
			view: view.id
		});
	};

	switchEngagementsMode = mode => {
		this.update({ engagementsMode: mode.id });
	};

	renderEngagementsModeSwitch() {
		const { engagementsMode } = this.state;
		return (
			<Tabs className="client-details__engagements-mode ft-engagements-mode" onSelect={this.switchEngagementsMode} selected={engagementsMode}>
				<Tab id={"workflow"} icon={<Icon.BookOpen size={16} strokeWidth={1.5} />} text="Engagements" />
				<Tab id={"backoffice"} icon={<Icon.Briefcase size={16} strokeWidth={1.5} />} text="Backoffice" />
			</Tabs>
		);
	}

	onShowModal = () => {
		this.update({
			showModal: true
		});
	};

	onOpenScheduleModal = () => {
		this.update({ showScheduleModal: true });
	};

	onCloseScheduleModal = () => {
		this.update({ showScheduleModal: false });
	};

	onCloseModal = ({ confirmed }) => {
		if (confirmed) {
			if (this.isNewClient()) {
				this.createClient();
			} else {
				this.updateClient();
			}
		}

		this.update({
			showModal: false
		});
	};

	fetchCustomFields = async () => {
		let { clientId } = this.state;

		let customFields = await ClientService.fetchCustomFields({ clientId });

		if (UtilityService.isNotAllowedResource({ response: customFields })) {
			this.update({
				customFields : []
			});	
		}
		else{
			this.update({
				customFields
			});
		}
	};

	onCustomFieldChange = ({ customField, newValue }) => {
		let { customFields } = this.state;

		customFields = customFields.slice();

		let [updatedCustomField] = customFields.filter(cf => cf.id === customField.id);

		updatedCustomField.value = newValue;

		this.update({
			customFields
		});
	};

	getMissingRequiredFields = () => {
		const { name, address, email, selectedPartnerName } = this.state;
		const missing = [];

		if (!name?.trim()) missing.push("Name");
		if (!address?.trim()) missing.push("Address");
		if (!email?.trim()) missing.push("Email");
		if (!selectedPartnerName?.trim()) missing.push("Partner");

		return missing;
	};
	
	renderCustomFields = () => {
		let { customFields } = this.state;

		return customFields.map((cf, index) => {
			if (cf.type === CLIENTS.customFieldTypes.string) {
				return (
					<TextFieldInput
						containerClass="details__general__input-cont"
						title={cf.name}
						name={cf.field}
						onChange={event => this.onCustomFieldChange({ customField: cf, newValue: event.target.value })}
						value={cf.value || ""}
						autoComplete="off"
						key={cf.name}
					/>
				);
			} else if (cf.type === CLIENTS.customFieldTypes.list) {
				return (
					<SearchableDropdown
						containerClass="details__general__input-cont"
						title={cf.name}
						type="customFieldList"
						customFieldId={cf.id}
						selectedName={cf.value}
						onSelect={item => this.onCustomFieldChange({ customField: cf, newValue: item.name })}
						showAll
						key={cf.name}
					/>
				);
			}

			return null;
		});
	};

	render() {
		let {
			// Display Items
			view,
			showModal,
			showDeleteModal,
			showNewGroupModal,
			newGroupName,
			creatingGroup,
			showScheduleModal,
			selectedTeamName,
			selectedPartnerName,
			selectedManagerName,
			selectedSeniorManagerName,
			selectedBookkeeperManagerName,
			selectedClientGroupName,
			selectedStatusName,

			// Client Data
			name,
			type,
			clientId,
			craBusinessNumber,
			operatingName,
			yearEnd,
			isHst,
			isPayroll,
			isEht,
			isWsib,
			email,
			description,
			address,
			city,
			stateProv,
			zipPostal,
			isNotAllowedResource,
			hstNumber,
			status,
			primaryContactPhone,
			primaryContactToggle
		} = this.state;

		if (isNotAllowedResource) 
		{
			return <RestrictedAccess />;
		}

		let { switchView } = this;

		const yearEndDate =
			typeof yearEnd === "string" && yearEnd
				? (() => {
						const [y, m, d] = yearEnd.split("-").map(Number);
						return new Date(y, m - 1, d);
				  })()
				: undefined;


		return (
			<>
				<div className="client-details">
					<div className="client-details__header">
						<PageHeading section="Clients" title={CLIENTS.clientTypes[type].value} />

						<div className="container-header-title__name">
							<div className="container-header-title__name__back" onClick={this.onBack}>
								<Icon.ArrowLeftCircle size={32} />
							</div>
							<span>{this.isNewClient() ? "New Client" : name}</span>
							{!this.isNewClient() && (
								<button
									className="client-details__schedule-btn"
									onClick={this.onOpenScheduleModal}
								>
									<Icon.Video size={16} />
									Schedule Meeting
								</button>
							)}
						</div>
					</div>

					{clientId !== "new" && (
						<Tabs onSelect={switchView} selected={view}>
							<Tab id={"details"} icon={<Icon.FileText size={18} strokeWidth={1.5} />} text="Details" />
							<PermissionGate resource="contact" action="list">
								<Tab id={"contacts"} icon={<Icon.Phone size={18} strokeWidth={1.5} />} text="Contacts" />
							</PermissionGate>
							<PermissionGate resource="engagement" action="list">
								<Tab id={"engagements"} icon={<Icon.BookOpen size={18} strokeWidth={1.5} />} text="Engagements" />
							</PermissionGate>
							<PermissionGate resource="task" action="list">							
								<Tab id={"tasks"} icon={<Icon.List size={18} strokeWidth={1.5} />} text="Tasks" />
							</PermissionGate>
							<PermissionGate resource="note" action="list">							
								<Tab id={"notes"} icon={<Icon.FileText size={18} strokeWidth={1.5} />} text="Notes" />
							</PermissionGate>
							<PermissionGate resource="note" action="list">							
								<Tab id={"references"} icon={<Icon.FileText size={18} strokeWidth={1.5} />} text="References" />
							</PermissionGate>
							<Tab id={"tools"} icon={<Icon.Settings size={18} strokeWidth={1.5} />} text="Tools" />
							<Tab id={"connections"} icon={<Icon.Settings size={18} strokeWidth={1.5} />} text="Connections" />
							<Tab id={"related"} icon={<Icon.Settings size={18} strokeWidth={1.5} />} text="Related" />
							<Tab id={"communications"} icon={<Icon.MessageSquare size={18} strokeWidth={1.5} />} text="Communications" />							
						</Tabs>

						// <div className="toggle">
						// <button className={currentView === "documents" ? "toggle--active" : ""} onClick={() => this.switchView("documents")}>Document Requests</button>
						// </div>
					)}

					{view === "details" && (
						<div className="details">
							<div className="details__general">
								<div className="details__general__title">General</div>
								{(type === CLIENTS.clientTypes.corporate.id || type === CLIENTS.clientTypes.personal.id) &&  (
									<TextFieldInput
										containerClass="details__general__input-cont"
										title={type === CLIENTS.clientTypes.personal.id ? "Social Insurance Number" : "CRA Business Number"}
										name="craBusinessNumber"
										onChange={this.onChange}
										value={craBusinessNumber || ""}
										autoComplete="off"
										maxCharactersAllowed={9}
									/>
								)}

								<TextFieldInput
									containerClass="details__general__input-cont"
									title="Operating Name"
									name="operatingName"
									onChange={this.onChange}
									value={operatingName || ""}
									autoComplete="off"
								/>

								{type !== CLIENTS.clientTypes.corporate.id && (
								<TextFieldInput
									containerClass="details__general__input-cont"
									title={"HST Number"}
									name="hstNumber"
									onChange={this.onChange}
									value={hstNumber || ""}
									autoComplete="off"
									type="text"
									maxCharactersAllowed={15}
									pattern="\d*"
								/>)}

								<TextFieldInput
									containerClass="details__general__input-cont"
									title={<RequiredLabel text="Name" />}
									name="name"
									onChange={this.onChange}
									value={name || ""}
									autoComplete="off"
								/>

								<div className="details__general__group-row">
									<SearchableDropdown
										containerClass="details__general__group-row__dropdown"
										title="Client Group"
										type={"clientGroups"}
										selectedName={selectedClientGroupName}
										onSelect={this.onClientGroupSelect}
										showAll
									/>
									<Button
										view="secondary"
										icon={<Icon.Plus size={20} />}
										onClick={this.onOpenNewGroupModal}
										className="details__general__group-row__add-btn"
									/>
								</div>

								<SearchableDropdown
									containerClass="details__general__input-cont"
									title="Team"
									onSelect={this.onSelectTeam}
									type={"teams"}
									selectedName={selectedTeamName}
									showAll
								/>

								<SearchableDropdown
									containerClass="details__general__input-cont"
									title={<RequiredLabel text="Partner" />}
									type={"partners"}
									selectedName={selectedPartnerName}
									onSelect={this.onPartnerSelect}
									showAll
								/>
								<SearchableDropdown
									containerClass="details__general__input-cont"
									title="Senior Manager"
									type={"users"}
									selectedName={selectedSeniorManagerName}
									onSelect={this.onSeniorManagerSelect}
									showAll
								/>
								{this.renderManagedByCheckbox(CLIENTS.managedByRoles.seniorManager)}

								<SearchableDropdown
									containerClass="details__general__input-cont"
									title="Manager"
									type={"users"}
									selectedName={selectedManagerName}
									onSelect={this.onManagerSelect}
									showAll
								/>
								{this.renderManagedByCheckbox(CLIENTS.managedByRoles.manager)}

								{type === CLIENTS.clientTypes.corporate.id && (
									<>
										<SearchableDropdown
											containerClass="details__general__input-cont"
											title="Bookkeeper Manager"
											type={"users"}
											selectedName={selectedBookkeeperManagerName}
											onSelect={this.onBookkeeperManagerSelect}
										/>
										{this.renderManagedByCheckbox(CLIENTS.managedByRoles.bookkeeperManager)}
									</>
								)}

								<SearchableDropdown
									containerClass="details__general__input-cont"
									title="Status"
									type="status"
									onSelect={this.onStatusSelect}
									selectedName={selectedStatusName}
									showAll
								/>

								<DateSelector
									containerClass="details__general__input-cont"
									title="Year End"
									selectedDate={yearEndDate}
									onDateSelect={this.onYearEndSelect}
									dateFormat="MMMM d"
									showYearDropdown={false}
								/>

								<Checkbox containerClass="details__general__input-cont" title="HST" name="isHst" checked={isHst} onChange={this.onChange} />

								{type === CLIENTS.clientTypes.corporate.id && (
									<>
										<Checkbox
											containerClass="details__general__input-cont"
											title="Payroll"
											name="isPayroll"
											checked={isPayroll}
											onChange={this.onChange}
										/>
										<Checkbox
											containerClass="details__general__input-cont"
											title="EHT"
											name="isEht"
											checked={isEht}
											onChange={this.onChange}
										/>
										<Checkbox
											containerClass="details__general__input-cont"
											title="WSIB"
											name="isWsib"
											checked={isWsib}
											onChange={this.onChange}
										/>
									</>
								)}

								<TextFieldInput
									containerClass="details__general__input-cont"
									title={<RequiredLabel text="Email" />}
									name="email"
									onChange={this.onChange}
									value={email || ""}
									autoComplete="off"
								/>
								<TextFieldInput
									containerClass="details__general__input-cont"
									title="Description"
									name="description"
									onChange={this.onChange}
									value={description || ""}
									autoComplete="off"
								/>

								<TextAreaField
									containerClass="details__general__input-cont"
									title={<RequiredLabel text="Address" />}
									type="text"
									name="address"
									value={address || ""}
									autoComplete="off"
									onChange={this.onChange}
								/>

								<TextFieldInput
									containerClass="details__general__input-cont"
									title="City"
									name="city"
									onChange={this.onChange}
									value={city || ""}
									autoComplete="off"
								/>

								<TextFieldInput
									containerClass="details__general__input-cont"
									title="Province"
									name="stateProv"
									onChange={this.onChange}
									value={stateProv || ""}
									autoComplete="off"
								/>

								<TextFieldInput
									containerClass="details__general__input-cont"
									title="Zip Code"
									name="zipPostal"
									onChange={this.onChange}
									value={zipPostal || ""}
									autoComplete="off"
								/>

								{type === CLIENTS.clientTypes.personal.id && (
									<>
										<TextFieldInput
											containerClass="details__general__input-cont"
											title="Primary Contact Phone"
											name="primaryContactPhone"
											onChange={this.onChange}
											value={primaryContactPhone || ""}
											autoComplete="off"
										/>
										<Checkbox
											containerClass="details__general__input-cont"
											title="Set as Primary Contact"
											name="primaryContactToggle"
											checked={primaryContactToggle}
											onChange={this.onChange}
										/>
									</>
								)}

							</div>
							<div className="details__more">
								<div className="details__more__title">More</div>
								{this.renderCustomFields()}
							</div>
							<div className="details__actions">
								<PermissionGate resource="client" action="delete">
									{!this.isNewClient() && <Button view="secondary" icon={<Icon.Trash2 size={22} />} onClick={this.onConfirmModalOpen} />}
								</PermissionGate>
								<PermissionGate resource="client" action="create">
									{this.isNewClient() && <Button text={"Create"} onClick={this.onShowModal} disabled={this.state.loading} />}
								</PermissionGate>
								<PermissionGate resource="client" action="update">
									{!this.isNewClient() && <Button text={"Update"} onClick={this.onShowModal} disabled={this.state.loading} />}
								</PermissionGate>
							</div>
						</div>
					)}
				</div>

				{view === "engagements" && clientId !== "new" && parseInt(clientId) > 0 && this.state.engagementsMode !== "backoffice" && (
					<Engagements title={`Engagements for ${name}`} clientId={clientId} showViews={false} showFilters={false} headerLeft={this.renderEngagementsModeSwitch()} {...this.props} />
				)}

				{view === "engagements" && clientId !== "new" && parseInt(clientId) > 0 && this.state.engagementsMode === "backoffice" && (
					<BackofficeEngagements title={`Backoffice engagements for ${name}`} clientId={parseInt(clientId)} headerLeft={this.renderEngagementsModeSwitch()} />
				)}

				{view === "contacts" && parseInt(clientId) > 0 && <Contacts title={"Contacts"} clientId={clientId} {...this.props} />}

				{/* {view === "documents" && parseInt(this.state.clientId) > 0 && (<DocumentRequests
                    title={"Document Requests"}
                    clientId={this.state.clientId}
                    {...this.props}
                />)} */}

				{view === "notes" && parseInt(clientId) > 0 && <Notes title="Notes" pinnedOnly={true} clientId={clientId} {...this.props} />}

				{view === "references" && parseInt(clientId) > 0 && (
					<Notes title="References" pinnedOnly={true} clientId={clientId} {...this.props} type="reference" />
				)}

				{view === "tasks" && parseInt(clientId) > 0 && (
					<Tasks title="Tasks" clientId={clientId} showViews={false} showFilters={false} {...this.props} />
				)}

				{view === "tools" && <Tools clientId={clientId} clientName={this.state.name} onOpenConnections={() => this.switchView({ id: "connections" })} {...this.props} />}
				{view === "connections" && <Connections clientId={clientId} {...this.props} />}
				{view === "related" && <ClientGroups clientId={clientId} />}
				{view === "communications" && parseInt(clientId) > 0 && <CommunicationLog clientId={clientId} {...this.props} />}

				<Modal open={showModal} center>
					<div className="modal-title">{this.isNewClient() ? "Create Client?" : "Update Client?"}</div>
					<div className="modal-content">
						{!this.isNewClient() && status === "inactive" && (
							<div className="modal-warning client-details__inactivate-warning">
								Warning: all active recurring engagements associated with this client will be inactivated.
							</div>
						)}

						<div>Are you sure you want to {this.isNewClient() ? "create" : "update"} this client?</div>
					</div>
					<div className="modal-footer">
						<Button view="secondary" text="No" onClick={() => this.onCloseModal({ confirmed: false })} />
						<Button text="Yes" onClick={() => this.onCloseModal({ confirmed: true })} />
					</div>
				</Modal>

				<Modal open={showDeleteModal} center>
					<div className="modal-title">Delete Client?</div>
					<div className="modal-content">Are you sure you want to remove this client?</div>
					<div className="modal-footer">
						<Button
							view="secondary"
							text="No"
							onClick={() => {
								this.onCloseDeleteModal({ confirmed: false });
							}}
						/>
						<Button text="Yes" onClick={() => this.onCloseDeleteModal({ confirmed: true })} />
					</div>
				</Modal>

				<Modal open={showNewGroupModal} onClose={this.onCloseNewGroupModal} center>
					<div className="modal-title">New Client Group</div>
					<div className="modal-content">
						<TextFieldInput
							title="Client Group Name"
							name="newGroupName"
							onChange={this.onChange}
							value={newGroupName || ""}
							autoComplete="off"
						/>
					</div>
					<div className="modal-footer">
						<Button view="secondary" text="Cancel" onClick={this.onCloseNewGroupModal} />
						<Button text={creatingGroup ? "Creating..." : "Create"} onClick={this.onCreateNewGroup} />
					</div>
				</Modal>
				<ScheduleMeetingModal
					show={showScheduleModal}
					clientId={clientId}
					clientName={name}
					clientEmail={this.state.email}
					onClose={this.onCloseScheduleModal}
				/>
			</>
		);
	}
}

export default ClientDetails;
